/**
 * Technikal motion layer (styles in assets/tk-motion.css).
 * Ports the movement measured on Solara's live product page:
 *  - card rows slide in one by one as they scroll into view
 *  - line icons draw themselves in; USP icons "play" each time their card comes into view
 *  - photos shimmer until loaded
 * Works on every page (homepage, product, collection) and on sections Shopify re-renders
 * (theme editor, variant change), because new elements are picked up by a MutationObserver.
 */

const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
if (!reduce && 'IntersectionObserver' in window) document.documentElement.classList.add('tk-motion');

/** Rows whose children slide in one after another: [container, children]. */
const STAGGER = ['.tk-tb__items', '.tk-ps__track', '.tk-tabs__row', '.tk-vs__track', '.tk-faq__list'];
/** Line icons that draw themselves in. */
const DRAW = ['.tk-usp__icon svg', '.tk-cmp__icon svg', 'svg.tk-tb__icon'];
/** Picture frames that shimmer until the photo arrives. */
const SKEL = ['.tk-ps__img', '.tk-tabs__img', '.tk-sp-strip__track a', '.tk-sp-strip__track figure'];

const inView = reduce ? null : new IntersectionObserver((entries) => {
  for (const { isIntersecting, target } of entries) {
    if (!isIntersecting) continue;
    target.classList.add('is-in');
    inView.unobserve(target);
  }
}, { rootMargin: '0px 0px -10% 0px', threshold: 0.12 });

// USP cards replay their icon animation every time they come back into view (Solara toggles is-play).
const play = reduce ? null : new IntersectionObserver((entries) => {
  for (const { isIntersecting, target } of entries) target.classList.toggle('is-play', isIntersecting);
}, { threshold: 0.6 });

function setup(root = document) {
  for (const sel of STAGGER) {
    root.querySelectorAll(`${sel}:not([data-tk-m])`).forEach((row) => {
      row.setAttribute('data-tk-m', '');
      [...row.children].forEach((child, i) => child.style.setProperty('--tk-i', Math.min(i, 8)));
      if (inView) inView.observe(row);
      else row.classList.add('is-in');
    });
  }
  for (const sel of DRAW) {
    root.querySelectorAll(`${sel}:not([data-tk-draw])`).forEach((svg) => {
      svg.setAttribute('data-tk-draw', '');
      // pathLength=1 lets one dasharray value work for every shape, whatever its real length
      svg.querySelectorAll('path, circle, rect, line, polyline, polygon, ellipse').forEach((shape) => shape.setAttribute('pathLength', '1'));
      const holder = svg.closest('.tk-usp__card, .tk-cmp__row, .tk-tb__item');
      const parentRow = holder?.parentElement;
      if (holder && parentRow) holder.style.setProperty('--tk-i', Math.min([...parentRow.children].indexOf(holder), 8));
      // trust badges / USP cards sit inside rows we already watch; compare rows sit inside the
      // revealed table. Anything else gets its own watcher.
      if (!svg.closest('[data-tk-m], [data-tk-reveal]')) {
        const wrap = svg.parentElement;
        if (inView) inView.observe(wrap); else wrap.classList.add('is-in');
      }
    });
  }
  root.querySelectorAll('.tk-usp__card:not([data-tk-play])').forEach((card, i) => {
    card.setAttribute('data-tk-play', '');
    card.style.setProperty('--tk-i', i % 4);
    if (play) play.observe(card);
  });
  for (const sel of SKEL) {
    root.querySelectorAll(`${sel}:not([data-tk-skel])`).forEach((frame) => {
      const img = frame.querySelector('img');
      if (!img) return;
      frame.setAttribute('data-tk-skel', '');
      const done = () => frame.classList.add('is-loaded');
      if (img.complete && img.naturalWidth) done();
      else { img.addEventListener('load', done, { once: true }); img.addEventListener('error', done, { once: true }); }
    });
  }
}

// USP cards sit in a [data-tk-reveal] row (tk-pdp.js reveals it); make its icons draw when that happens.
// The CSS handles `.is-revealed [data-tk-draw]` as well as `.is-in [data-tk-draw]`.

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => setup());
else setup();

// Sections re-rendered by Shopify (editor, variant switch, cart) bring new elements: set them up too.
let pending = false;
new MutationObserver(() => {
  if (pending) return;
  pending = true;
  requestAnimationFrame(() => { pending = false; setup(); });
}).observe(document.body, { childList: true, subtree: true });
document.addEventListener('shopify:section:load', (event) => setup(event.target));

/* ---------- typing headline (sections/tk-hero.liquid, [data-tk-type]) ----------
   The full text stays in the DOM (ghost span) for SEO, screen readers and layout; the live span types it
   out once on load, then the cursor keeps blinking. Reduced motion: text shown at once. */
function typeIn(el) {
  if (el.dataset.tkTyped) return;
  el.dataset.tkTyped = '1';
  const ghost = el.querySelector('.tk-type__ghost');
  const live = el.querySelector('.tk-type__live');
  if (!ghost || !live) return;
  const text = ghost.textContent.trim();
  if (reduce) { ghost.style.color = 'inherit'; live.remove(); return; }
  let i = 0;
  const tick = () => {
    live.textContent = text.slice(0, ++i);
    if (i < text.length) setTimeout(tick, text[i - 1] === '.' ? 260 : 70 + Math.random() * 50);
  };
  setTimeout(tick, 450);
}
const typeAll = () => document.querySelectorAll('[data-tk-type]').forEach(typeIn);
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', typeAll); else typeAll();
document.addEventListener('shopify:section:load', typeAll);
